import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { getClerkLocalization } from './utils/clerkLocalization';
import { initPostHog } from './lib/posthog';
import { initMetaPixel } from './lib/metaPixel';
import './index.css';
import App from './App.jsx';
import ErrorBoundary from './components/ErrorBoundary';

// CRITICAL: Save native fetch BEFORE Sentry initializes
// Sentry patches the global fetch, which can cause CORS issues with third-party APIs
// We store the original fetch so we can use it for external uploads
window.__nativeFetch = window.fetch.bind(window);

// Initialize Sentry for frontend
const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const isProduction = import.meta.env.PROD;
const environment = import.meta.env.MODE || 'development';

if (sentryDSN && sentryDSN.trim().length > 0) {
  try {
    console.log('[Sentry] Initializing Sentry for frontend');
    console.log('[Sentry] Environment:', environment);
    console.log('[Sentry] Is Production:', isProduction);

    Sentry.init({
      dsn: sentryDSN,
      environment: environment,
      enableLogs: true,
      integrations: [
        Sentry.browserTracingIntegration({
          // Completely disable fetch instrumentation to prevent CORS issues with third-party APIs
          traceFetch: false,
          traceXHR: true, // Keep XHR tracing for our own API calls
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        Sentry.consoleLoggingIntegration({
          levels: ["log", "error", "warn"],
        }),
      ],
      // Disable ALL trace header propagation to prevent CORS issues
      tracePropagationTargets: [],
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Add release information if available
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      // Filter out Sentry's own "Load failed" errors and other non-critical errors
      ignoreErrors: [
        // Sentry SDK errors - these are internal Sentry issues, not application errors
        'Load failed',
        'Failed to fetch',
        'NetworkError',
        'Network request failed',
        // Browser extension errors (not our fault)
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        // Third-party script errors
        'Script error',
        'Javascript error',
      ],
      // Filter out errors in beforeSend hook
      beforeSend(event, hint) {
        // Ignore Sentry's own load errors
        if (event.message && (
          event.message.includes('Load failed') ||
          event.message.includes('Failed to fetch') ||
          event.message.includes('NetworkError')
        )) {
          // Check if it's a Sentry SDK error (from sentry.io domain)
          const errorUrl = event.request?.url || hint.originalException?.stack || '';
          if (errorUrl.includes('sentry') || errorUrl.includes('bodai.io/assets/js/sentry')) {
            console.warn('[Sentry] Ignoring Sentry SDK load error:', event.message);
            return null; // Don't send to Sentry
          }
        }
        
        // Ignore browser extension errors
        if (event.exception?.values?.[0]?.type === 'Error' && 
            event.exception.values[0].value?.includes('ResizeObserver')) {
          return null;
        }
        
        return event;
      },
    });

    console.log('[Sentry] Sentry initialized successfully');
  } catch (error) {
    console.error('[Sentry] Failed to initialize Sentry:', error);
  }
} else {
  console.log('[Sentry] VITE_SENTRY_DSN not found or empty, skipping Sentry initialization');
  if (import.meta.env.DEV) {
    console.log('[Sentry] Available env vars:', Object.keys(import.meta.env).filter(k => k.includes('SENTRY')));
  }
}

// Initialize PostHog analytics
initPostHog();

// Initialize Meta Pixel for Facebook Ads tracking
initMetaPixel();

// Track performance metrics after PostHog loads
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    // Wait a bit for PostHog to be ready
    setTimeout(() => {
      const posthog = window.posthog;
      if (posthog && window.performance && window.performance.timing) {
        const perf = window.performance.timing;
        const pageLoadTime = perf.loadEventEnd - perf.navigationStart;
        const domContentLoaded = perf.domContentLoadedEventEnd - perf.navigationStart;
        const timeToFirstByte = perf.responseStart - perf.navigationStart;

        posthog.capture('page_performance', {
          page_load_time: pageLoadTime,
          dom_content_loaded: domContentLoaded,
          time_to_first_byte: timeToFirstByte,
          url: window.location.href,
        });
      }
    }, 1000);
  });
}



// For Vite, use VITE_ prefix for environment variables
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key. Please set VITE_CLERK_PUBLISHABLE_KEY in your .env file');
}

// Custom Clerk appearance to match BodAI design system
const clerkAppearance = {
  variables: {
    // Colors
    colorPrimary: '#611BBD',
    colorTextOnPrimaryBackground: '#FFFFFF',
    colorBackground: '#FFFFFF',
    colorInputBackground: '#F8FAFC',
    colorInputText: '#0F172A',
    colorText: '#0F172A',
    colorTextSecondary: '#64748B',
    colorDanger: '#EF4444',
    colorSuccess: '#10B981',
    colorWarning: '#F59E0B',

    // Typography - Heebo for Hebrew, Inter fallback
    fontFamily: "'Heebo', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontFamilyButtons: "'Heebo', 'Inter', system-ui, sans-serif",
    fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 },

    // Sizing & Spacing
    borderRadius: '12px',
    spacingUnit: '16px',

    // Shadows
    shadowShimmer: '0 0 20px rgba(97, 27, 189, 0.1)',
  },
  elements: {
    // Root & Card
    rootBox: {
      fontFamily: "'Heebo', 'Inter', system-ui, sans-serif",
    },
    card: {
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
      borderRadius: '1rem',
      border: '1px solid #E2E8F0',
      backdropFilter: 'blur(8px)',
    },

    // Header
    headerTitle: {
      fontFamily: "'Space Grotesk', system-ui, sans-serif",
      fontWeight: 700,
      fontSize: '24px',
      color: '#0F172A',
    },
    headerSubtitle: {
      color: '#64748B',
      fontSize: '15px',
    },

    // Social Buttons
    socialButtonsBlockButton: {
      borderRadius: '10px',
      border: '1px solid #E2E8F0',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#F8FAFC',
        borderColor: '#CBD5E1',
      },
    },
    socialButtonsBlockButtonText: {
      fontWeight: 500,
      color: '#0F172A',
    },

    // Divider
    dividerLine: {
      backgroundColor: '#E2E8F0',
    },
    dividerText: {
      color: '#94A3B8',
      fontSize: '13px',
    },

    // Form Fields
    formFieldLabel: {
      fontWeight: 500,
      color: '#334155',
      fontSize: '14px',
      marginBottom: '6px',
    },
    formFieldInput: {
      borderRadius: '10px',
      border: '1px solid #E2E8F0',
      padding: '12px 14px',
      fontSize: '15px',
      transition: 'all 0.2s ease',
      '&:focus': {
        borderColor: '#611BBD',
        boxShadow: '0 0 0 3px rgba(97, 27, 189, 0.1)',
      },
    },

    // Primary Button - Premium gradient and shadow
    formButtonPrimary: {
      background: 'linear-gradient(135deg, #611BBD 0%, #4C1A95 100%)',
      borderRadius: '10px',
      padding: '12px 20px',
      fontWeight: 600,
      fontSize: '15px',
      textTransform: 'none',
      boxShadow: '0 4px 16px rgba(97, 27, 189, 0.35)',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'linear-gradient(135deg, #4C1A95 0%, #3B1369 100%)',
        boxShadow: '0 6px 20px rgba(97, 27, 189, 0.45)',
        transform: 'translateY(-1px)',
      },
    },

    // Footer Links
    footerActionLink: {
      color: '#611BBD',
      fontWeight: 500,
      '&:hover': {
        color: '#4C1A95',
      },
    },
    footerActionText: {
      color: '#64748B',
    },

    // Identity Preview
    identityPreviewText: {
      color: '#0F172A',
    },
    identityPreviewEditButton: {
      color: '#611BBD',
    },

    // Alerts
    alert: {
      borderRadius: '10px',
    },
    alertText: {
      color: '#0F172A',
    },

    // User Button
    userButtonBox: {
      borderRadius: '10px',
    },
    userButtonTrigger: {
      borderRadius: '10px',
    },
    userButtonPopoverCard: {
      borderRadius: '14px',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
    },
    userButtonPopoverActionButton: {
      borderRadius: '8px',
    },

    // Modal Backdrop
    modalBackdrop: {
      backgroundColor: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(4px)',
    },
  },
  layout: {
    socialButtonsPlacement: 'top',
    socialButtonsVariant: 'iconButton',
    showOptionalFields: false,
    logoPlacement: 'inside',
    helpPageUrl: 'mailto:support@bodai.com',
    privacyPageUrl: '/privacy',
    termsPageUrl: '/terms',
  },
};



// Wrapper component to provide dynamic localization based on current language
function ClerkProviderWrapper({ children }) {
  const { i18n: i18nHook } = useTranslation();
  const [localization, setLocalization] = React.useState(() => {
    // Get initial language - prefer resolvedLanguage as it's more reliable
    const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
    return getClerkLocalization(currentLang);
  });

  // Update localization when language changes
  React.useEffect(() => {
    const currentLang = i18nHook.resolvedLanguage || i18nHook.language || 'en';
    setLocalization(getClerkLocalization(currentLang));
  }, [i18nHook.language, i18nHook.resolvedLanguage]);

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      fallbackRedirectUrl="/dashboard"
      appearance={clerkAppearance}
      localization={localization}
    >
      {children}
    </ClerkProvider>
  );
}

// Wrap App with Sentry Profiler for performance monitoring
const ProfiledApp = Sentry.withProfiler(App);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <ErrorBoundary>
    <HelmetProvider>
      <ClerkProviderWrapper>
        <BrowserRouter>
          <ProfiledApp />
        </BrowserRouter>
      </ClerkProviderWrapper>
    </HelmetProvider>
  </ErrorBoundary>
);

